/**
 * AplicarImagenModal — permite reutilizar la foto de un producto en varios
 * productos destino. No copia el archivo: apunta al mismo `imagen_path`.
 *
 * El backend protege el archivo compartido: no se borra del storage al
 * reemplazar/eliminar la foto de un producto si otros la siguen usando.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Check, Copy, ImageOff, Image as ImageIcon } from "lucide-react";

export default function AplicarImagenModal({ open, onOpenChange, sourceId, sourceNombre, onApplied }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [productos, setProductos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [aplicando, setAplicando] = useState(false);
  const [soloSinFoto, setSoloSinFoto] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!open) {
      setQ(""); setSelected(new Set()); setSoloSinFoto(true);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/productos", { params: { limit: 2000 } });
        setProductos(Array.isArray(data) ? data : (data?.productos || []));
      } catch (e) {
        toast.error("No se pudo cargar la lista");
      } finally { setLoading(false); }
    })();
  }, [open]);

  const filtrados = useMemo(() => {
    const term = q.trim().toLowerCase();
    return productos
      .filter((p) => p.id !== sourceId)
      .filter((p) => (soloSinFoto ? !p.imagen_path : true))
      .filter((p) => !term || (p.nombre || "").toLowerCase().includes(term) || (p.sku || "").toLowerCase().includes(term))
      .slice(0, 400);
  }, [productos, q, sourceId, soloSinFoto]);

  const toggle = (id) => {
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtrados.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtrados.map((p) => p.id)));
    }
  };

  const aplicar = async () => {
    if (selected.size === 0) return toast.error("Selecciona al menos un producto");
    setAplicando(true);
    try {
      const { data } = await api.post(`/productos/${sourceId}/aplicar-imagen-a`, {
        producto_ids: Array.from(selected),
      });
      toast.success(`Foto aplicada a ${data.aplicados} producto${data.aplicados === 1 ? "" : "s"}`);
      onApplied?.(data.aplicados);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar");
    } finally { setAplicando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="aplicar-imagen-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
              <Copy className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase text-blue-700 tracking-wider">Aplicar foto</div>
              <DialogTitle className="font-display text-xl mt-1">Reutilizar esta foto en otros productos</DialogTitle>
              {sourceNombre && (
                <p className="text-xs text-muted-foreground mt-1">Foto origen: <b>{sourceNombre}</b></p>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex items-center gap-2 mt-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o SKU"
              className="pl-9 h-10"
              data-testid="aplicar-imagen-buscar"
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
            <input type="checkbox" checked={soloSinFoto} onChange={(e) => setSoloSinFoto(e.target.checked)} data-testid="aplicar-imagen-solo-sin-foto" />
            Solo sin foto
          </label>
        </div>

        <div className="flex items-center justify-between text-xs mt-2 mb-1 px-1">
          <span className="text-muted-foreground">
            {filtrados.length} producto{filtrados.length === 1 ? "" : "s"} · Seleccionados: <b>{selected.size}</b>
          </span>
          <button
            type="button"
            onClick={toggleAll}
            className="text-primary hover:underline text-xs"
            data-testid="aplicar-imagen-toggle-all"
          >
            {selected.size === filtrados.length && filtrados.length > 0 ? "Deseleccionar todos" : "Seleccionar todos"}
          </button>
        </div>

        <div className="flex-1 border border-border rounded-lg overflow-y-auto min-h-0" data-testid="aplicar-imagen-lista">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground text-sm">Cargando productos...</div>
          ) : filtrados.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              {soloSinFoto ? "No hay productos sin foto que coincidan." : "Sin resultados."}
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {filtrados.map((p) => {
                const isSel = selected.has(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggle(p.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/40 transition-colors ${isSel ? "bg-blue-50 dark:bg-blue-950/20" : ""}`}
                    data-testid={`aplicar-imagen-row-${p.id}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSel ? "bg-blue-600 border-blue-600" : "border-border"}`}>
                      {isSel && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div className="w-10 h-10 rounded border border-border bg-muted flex-shrink-0 overflow-hidden flex items-center justify-center">
                      {p.imagen_path ? (
                        <img
                          src={`${backendUrl}/api/catalogo/img/${p.id}?w=200`}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      ) : (
                        <ImageOff className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.nombre}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {p.sku && <span className="font-mono mr-2">{p.sku}</span>}
                        {p.categoria_nombre || p.categoria || ""}
                      </div>
                    </div>
                    {p.imagen_path && (
                      <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" /> Ya tiene
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-between items-center mt-3 pt-3 border-t border-border">
          <p className="text-[11px] text-muted-foreground">
            Los productos seleccionados que ya tenían foto <b>serán reemplazados</b>.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              onClick={aplicar}
              disabled={aplicando || selected.size === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="aplicar-imagen-confirmar-btn"
            >
              {aplicando ? "Aplicando..." : `Aplicar a ${selected.size} producto${selected.size === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
